import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { memo } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { ToolCardImage } from "./tool-card/ToolCardImage";
import { ToolCardHeader } from "./tool-card/ToolCardHeader";
import { ToolCardActions } from "./tool-card/ToolCardActions";
import { ToolCardCategories } from "./tool-card/ToolCardCategories";
import { useToolCard } from "./tool-card/useToolCard";
import { getStyleForId } from "./tool-card/styles";

interface ToolCardProps {
  id: string;
  name: string;
  description: string;
  categories: string[];
  imageUrl: string;
  likes?: number;
  compact?: boolean;
  is_featured?: boolean;
}

export const ToolCard = memo(({ 
  id, 
  name, 
  description, 
  categories, 
  imageUrl, 
  likes = 0,
  compact = false,
  is_featured = false
}: ToolCardProps) => {
  const { likeCount, hasLiked, handleLike, handleCardClick } = useToolCard(id, likes);
  const style = getStyleForId(id);
  
  const cardClasses = `
    relative overflow-hidden border ${style.border} ${style.bg} ${style.shadow}
    transition-all duration-300 cursor-pointer backdrop-blur-sm
    ${compact ? 'max-w-3xl mx-auto' : ''}
    rounded-lg sm:rounded-xl h-[300px] sm:h-[350px]
  `;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      className="card-container"
    >
      <Card className={cardClasses} onClick={handleCardClick}>
        {is_featured && (
          <div className="absolute top-2 right-2 z-10">
            <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-yellow-400" />
          </div>
        )}
        <ToolCardImage 
          imageUrl={imageUrl} 
          name={name} 
          compact={compact} 
        />
        <ToolCardHeader 
          name={name} 
          compact={compact} 
        />
        <CardContent className="flex flex-col h-[calc(100%-150px)]">
          <CardDescription className="text-right font-arabic text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2 dark:text-gray-300">
            {description}
          </CardDescription>
          <div className="flex items-center justify-between gap-2 sm:gap-3 mt-auto">
            <ToolCardActions 
              id={id}
              name={name}
              description={description}
              likeCount={likeCount}
              hasLiked={hasLiked}
              onLike={handleLike}
            />
            <ToolCardCategories categories={categories} />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
});

ToolCard.displayName = 'ToolCard';